import apiClient from './client';
import type { ApiResponse } from '@/types';

/**
 * 推荐返佣 / 我的推广 API v2.0.44
 *
 * ⚠️ 字段约定（项目通用坑）：
 *  - 响应经服务端 snakeToCamel 中间件转换 → 前端读到的是 camelCase（inviteCode / commissionRate / pointsShared ...）
 *  - 请求体后端按 snake_case 解析；本模块提交比例时直接传数字 rate（后端已兼容 rate / commission_rate 两种字段名）
 */

// ---------------- 类型 ----------------

/** 平台全局推荐配置（所有登录用户可读，用于前端展示上限） */
export interface ReferralConfig {
  enabled: boolean;
  /** 平台默认分润比例 0~1 */
  defaultRate: number;
  /** 自定义比例上限 0~1 */
  maxRate: number;
  /** 是否允许推广者自定义比例 */
  allowCustomRate: boolean;
  /** 二级分销（两级返佣）总开关 */
  level2Enabled: boolean;
  /** 二级分润比例 0~1（平台统一设置，推广者不可自定义） */
  level2Rate: number;
}

/** 我的推广概览 */
export interface MyReferralStats {
  /** 个人推广码 U-XXXXXXXX（按需生成） */
  inviteCode: string;
  /** 平台是否开启推荐返佣 */
  enabled: boolean;
  /** 实际生效比例 0~1（默认比例或被自定义覆盖后） */
  commissionRate: number;
  /** 自定义比例（未设置则为 null，回落默认） */
  customRate: number | null;
  /** 平台默认比例 */
  defaultRate: number;
  /** 平台允许的最高比例 */
  maxRate: number;
  allowCustomRate: boolean;
  /** 推荐人数（直接下线） */
  referralCount: number;
  /** 分润笔数 */
  orderCount: number;
  /** 累计分润积分 */
  totalShared: number;
  /** 近 30 天分润积分 */
  recentShared: number;
  /** 当前积分余额 */
  pointsBalance: number;
  /** 二级分销开关（平台全局） */
  level2Enabled: boolean;
  /** 二级分润比例 0~1 */
  level2Rate: number;
  /** 二级下线数（我的直接下线的下线） */
  level2DownlineCount: number;
  /** 我作为二级推荐人获得的累计分润积分 */
  level2Shared: number;
}

/** 我推荐的下线用户 */
export interface MyReferralItem {
  id: string;
  displayName: string | null;
  /** 已脱敏邮箱 */
  email: string;
  identity: string | null;
  joinedAt: string;
  /** 该下线贡献的累计分润积分 */
  contributed: number;
}

export interface MyReferralList {
  total: number;
  page: number;
  pageSize: number;
  list: MyReferralItem[];
}

/** 我的二级下线用户（我的直接下线的下线） */
export interface MyReferralLevel2Item {
  id: string;
  displayName: string | null;
  email: string;
  identity: string | null;
  /** 其直接上线（即我的直接下线）id */
  parentId: string;
  /** 其直接上线显示名 */
  parentName: string;
  joinedAt: string;
  /** 该下线贡献给我的二级分润积分 */
  contributed: number;
}

export interface MyReferralLevel2List {
  total: number;
  page: number;
  pageSize: number;
  list: MyReferralLevel2Item[];
}

/** 管理端：全平台分润总览（含 level 标识 L1/L2） */
export interface AdminReferralOverviewItem {
  id: string;
  referrerName: string;
  consumerName: string;
  /** 分润层级：1=直接推荐(L1) / 2=二级推荐(L2) */
  level: number;
  sourceType: string;
  sourceLabel: string;
  pointsBase: number;
  pointsShared: number;
  commissionRate: number;
  createdAt: string;
}

export interface AdminReferralOverview {
  config: ReferralConfig;
  totalShared: number;
  total: number;
  page: number;
  pageSize: number;
  list: AdminReferralOverviewItem[];
}

/** 管理端：两级关系树节点 */
export interface AdminReferralTreeNode {
  id: string;
  displayName: string | null;
  email: string;
  identity: string | null;
  joinedAt?: string;
  l2: { id: string; displayName: string | null; email: string; identity: string | null }[];
}

export interface AdminReferralTree {
  root: { id: string; displayName: string | null; email: string };
  l1: AdminReferralTreeNode[];
}

/** 我的分润流水明细 */
export interface MyReferralDetail {
  id: string;
  consumerId: string;
  /** 消费者（被推荐人）显示名 */
  consumerName: string;
  /** 来源类型 recharge / template_ref / quotation_module / training_video / trial / exam_reward / other */
  sourceType: string;
  /** 来源中文标签 */
  sourceLabel: string;
  /** 计算基数积分 */
  pointsBase: number;
  /** 实际分润积分 */
  pointsShared: number;
  /** 分润比例 0~1 */
  commissionRate: number;
  /** 对应现金金额（充值场景，其余为 null） */
  cashAmount: number | null;
  status: string;
  createdAt: string;
}

export interface MyReferralDetailList {
  total: number;
  page: number;
  pageSize: number;
  list: MyReferralDetail[];
}

/** 我的分润明细逐条记录（含层级 level），供导出使用 */
export interface MyReferralShareDetail {
  id: string;
  /** 分润层级：1=直接推荐(L1) / 2=二级推荐(L2) */
  level: number;
  consumerId: string;
  /** 消费者（被推荐人）显示名 */
  consumerName: string;
  /** 消费者邮箱 */
  consumerEmail: string;
  consumerIdentity: string | null;
  sourceType: string;
  sourceLabel: string;
  pointsBase: number;
  pointsShared: number;
  commissionRate: number;
  cashAmount: number | null;
  status: string;
  createdAt: string;
}

export interface MyReferralShareDetailList {
  total: number;
  page: number;
  pageSize: number;
  list: MyReferralShareDetail[];
}

/** 管理端查看任意用户：被查看用户基本信息 */
export interface AdminUserReferralUser {
  id: string;
  displayName: string | null;
  email: string;
  phone: string | null;
  identity: string | null;
  isActive: boolean;
  referrerId: string | null;
  inviteCode: string | null;
}

/** 管理端查看任意用户：上线（推荐人）信息 */
export interface AdminUserReferralUpline {
  id: string;
  displayName: string | null;
  email: string;
  phone: string | null;
  identity: string | null;
  inviteCode: string | null;
}

/** 管理端查看任意用户的推广概览（在 MyReferralStats 基础上补充基本信息与上线） */
export interface AdminUserReferralStats extends MyReferralStats {
  user: AdminUserReferralUser;
  upline: AdminUserReferralUpline | null;
}

// ---------------- 上下线改派审批（推荐关系治理，v2.2.78） ----------------

/** 上下线改派申请 */
export interface DownlineAdjustmentRequest {
  id: string;
  requesterId: string;
  requesterName: string | null;
  /** 申请时快照：当前上线 */
  currentUplineId: string | null;
  /** 期望改绑到的目标上线 */
  requestedUplineId: string | null;
  requestedUplineName: string | null;
  reason: string | null;
  evidenceUrl: string | null;
  /** pending / approved / rejected / cancelled */
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  reviewerId: string | null;
  reviewerName: string | null;
  reviewNote: string | null;
  /** 核准生效真实快照：生效前 / 生效后 */
  appliedOldUplineId: string | null;
  appliedNewUplineId: string | null;
  createdAt: string;
  updatedAt: string;
}

/** 我的上线关系上下文 */
export interface DownlineMyContext {
  currentUplineId: string | null;
  currentUpline: { id: string; displayName: string | null; email: string | null } | null;
  /** 是否已有待审批的改派申请 */
  hasPending: boolean;
}

/** 管理端申请列表 */
export interface DownlineAdminList {
  total: number;
  page: number;
  pageSize: number;
  list: DownlineAdjustmentRequest[];
}

// ---------------- 封装 ----------------

const unwrap = async <T>(p: Promise<{ data: ApiResponse<T> }>): Promise<T> => {
  const resp = await p;
  return resp.data.data as T;
};

/** 公开解析推广码对应的推荐人（脱敏展示名）；游客进入页展示「由谁邀请」用 */
export interface ReferralResolve {
  exists: boolean;
  displayName: string;
}

export const referralApi = {
  /** 平台配置（上限 / 默认比例 / 是否允许自定义） */
  getConfig: () => unwrap<ReferralConfig>(apiClient.get('/referral/config')),

  /** 公开：按推广码解析推荐人（脱敏名），无需登录 */
  resolve: (code: string) =>
    unwrap<ReferralResolve>(apiClient.get('/referral/resolve', { params: { code } })),

  /** 我的推广概览（含推广码，按需生成） */
  getMyStats: () => unwrap<MyReferralStats>(apiClient.get('/referral/my/stats')),

  /** 我的推广码（幂等生成） */
  getMyInviteCode: () => unwrap<{ inviteCode: string }>(apiClient.get('/referral/my/invite-code')),

  /** 我推荐的用户（分页） */
  getMyReferrals: (page = 1, pageSize = 20) =>
    unwrap<MyReferralList>(
      apiClient.get('/referral/my/referrals', { params: { page, pageSize } })
    ),

  /** 我的分润流水（分页） */
  getMyDetails: (page = 1, pageSize = 20) =>
    unwrap<MyReferralDetailList>(
      apiClient.get('/referral/my/details', { params: { page, pageSize } })
    ),

  /** 我作为推荐人获得的逐条分润明细（level: 1=L1 / 2=L2，不传则全部），供导出 */
  getMyShares: (level?: 1 | 2, page = 1, pageSize = 50) =>
    unwrap<MyReferralShareDetailList>(
      apiClient.get('/referral/my/shares', { params: { level, page, pageSize } })
    ),

  /** 设置我的分润比例（rate 为 0~1 数字；传 null 表示恢复平台默认） */
  updateMyRate: (rate: number | null) =>
    unwrap<MyReferralStats>(apiClient.post('/referral/my/commission-rate', { rate })),

  /** 我的二级下线列表（分页） */
  getMyLevel2Referrals: (page = 1, pageSize = 20) =>
    unwrap<MyReferralLevel2List>(
      apiClient.get('/referral/my/referrals/level2', { params: { page, pageSize } })
    ),

  /** 管理端：全平台分润总览（含 L1/L2 层级标识） */
  getAdminOverview: (page = 1, pageSize = 20) =>
    unwrap<AdminReferralOverview>(
      apiClient.get('/referral/admin/overview', { params: { page, pageSize } })
    ),

  /** 管理端：指定用户的两级关系树 */
  getAdminTree: (userId: string, maxL1 = 50) =>
    unwrap<AdminReferralTree>(
      apiClient.get('/referral/admin/tree', { params: { userId, maxL1 } })
    ),

  /** 管理端：查看任意用户的推广概览（含上线，未脱敏） */
  getAdminUserStats: (userId: string) =>
    unwrap<AdminUserReferralStats>(
      apiClient.get('/referral/admin/user/stats', { params: { userId } })
    ),

  /** 管理端：查看任意用户的直接下线（未脱敏） */
  getAdminUserReferrals: (userId: string, page = 1, pageSize = 20) =>
    unwrap<MyReferralList>(
      apiClient.get('/referral/admin/user/referrals', { params: { userId, page, pageSize } })
    ),

  /** 管理端：查看任意用户的二级下线（未脱敏） */
  getAdminUserLevel2Referrals: (userId: string, page = 1, pageSize = 20) =>
    unwrap<MyReferralLevel2List>(
      apiClient.get('/referral/admin/user/referrals/level2', { params: { userId, page, pageSize } })
    ),

  /** 管理端：查看任意用户的分润流水 */
  getAdminUserDetails: (userId: string, page = 1, pageSize = 20) =>
    unwrap<MyReferralDetailList>(
      apiClient.get('/referral/admin/user/details', { params: { userId, page, pageSize } })
    ),

  /** 管理端：查看任意用户作为推荐人获得的逐条分润明细（level: 1=L1 / 2=L2，不传则全部） */
  getAdminUserShares: (userId: string, level?: 1 | 2, page = 1, pageSize = 50) =>
    unwrap<MyReferralShareDetailList>(
      apiClient.get('/referral/admin/user/shares', { params: { userId, level, page, pageSize } })
    ),

  /** 管理端：更新全局推荐返佣配置（含二级分销开关与比例） */
  updateAdminConfig: (payload: Partial<ReferralConfig>) =>
    unwrap<ReferralConfig>(apiClient.post('/referral/admin/config', payload)),

  // ===== 上下线改派审批（v2.2.78） =====

  /** 我的上线关系上下文（当前上线 + 是否有待审申请） */
  getDownlineContext: () =>
    unwrap<DownlineMyContext>(apiClient.get('/downline-adjustments/mine/context')),

  /** 发起改绑上线申请（targetInviteCode 或 targetUserId 二选一） */
  createDownlineRequest: (payload: { targetInviteCode?: string; targetUserId?: string; reason?: string; evidenceUrl?: string }) =>
    unwrap<DownlineAdjustmentRequest>(apiClient.post('/downline-adjustments', payload)),

  /** 我的改派申请列表 */
  listMyDownlineRequests: () =>
    unwrap<DownlineAdjustmentRequest[]>(apiClient.get('/downline-adjustments/mine')),

  /** 撤销本人 pending 申请 */
  cancelDownlineRequest: (id: string) =>
    unwrap<DownlineAdjustmentRequest>(apiClient.post(`/downline-adjustments/${id}/cancel`)),

  /** 管理端：申请列表（status 可选 pending/approved/rejected/cancelled） */
  adminListDownlineRequests: (status?: string, page = 1, pageSize = 20) =>
    unwrap<DownlineAdminList>(
      apiClient.get('/downline-adjustments/admin/list', { params: { status, page, pageSize } })
    ),

  /** 管理端：核准 / 驳回 */
  adminReviewDownlineRequest: (id: string, decision: 'approve' | 'reject', reviewNote?: string) =>
    unwrap<DownlineAdjustmentRequest>(
      apiClient.post(`/downline-adjustments/admin/${id}/review`, { decision, reviewNote })
    ),

  /** 管理端：管理员直接改派（无需本人申请，强制留审计） */
  adminDirectRebind: (payload: {
    userId: string;
    newReferrerId?: string | null;
    targetInviteCode?: string | null;
    reason?: string;
  }) =>
    unwrap<DownlineAdjustmentRequest>(
      apiClient.post('/downline-adjustments/admin/direct-rebind', payload)
    ),
};

export default referralApi;
